import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import {
  useCreateSettlement,
  useSettlements,
  type SettlementResponse,
} from "@/hooks/api/useSettlements";

function euro(v: number | null | undefined): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
}

function DirectionBadge({ direction }: { direction: SettlementResponse["direction"] }) {
  if (direction === "charge")
    return (
      <Badge tone="warning" dot>
        Charged tenant
      </Badge>
    );
  if (direction === "refund")
    return (
      <Badge tone="info" dot>
        Refund owed
      </Badge>
    );
  return (
    <Badge tone="neutral" dot>
      Even
    </Badge>
  );
}

/** Annual service-cost settlement (afrekening): compares the advances the tenant
 *  paid against the actual costs, then charges the shortfall or owes a refund. */
export function SettlementCard({ contractId }: { contractId: string }) {
  const { data: settlements } = useSettlements(contractId);
  const create = useCreateSettlement(contractId);
  const [open, setOpen] = useState(false);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [actual, setActual] = useState("");
  const [advanced, setAdvanced] = useState("");
  const [note, setNote] = useState("");

  const reset = () => {
    setPeriodStart("");
    setPeriodEnd("");
    setActual("");
    setAdvanced("");
    setNote("");
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!periodStart || !periodEnd || actual === "") {
      toast.error("Set the period and the actual costs");
      return;
    }
    create.mutate(
      {
        period_start: periodStart,
        period_end: periodEnd,
        total_actual: Number(actual),
        total_advanced: advanced === "" ? undefined : Number(advanced),
        note: note || undefined,
      },
      {
        onSuccess: (s) => {
          if (s.direction === "charge")
            toast.success(`Shortfall of ${euro(s.difference)} charged to the tenant`);
          else if (s.direction === "refund")
            toast.success(`Refund of ${euro(Math.abs(s.difference))} owed to the tenant`);
          else toast.success("Settlement recorded — nothing to charge");
          setOpen(false);
          reset();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const rows = settlements ?? [];

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Service cost settlement</CardTitle>
        {!open && (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            New settlement
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          The annual afrekening reconciles the service-cost advances the tenant paid
          against the actual costs. A shortfall is charged on a new invoice; an overpayment
          is refunded.
        </p>

        {open && (
          <form className="space-y-3 border-t border-border/60 pt-4" onSubmit={submit}>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="st-start">Period start</Label>
                <Input
                  id="st-start"
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="st-end">Period end</Label>
                <Input
                  id="st-end"
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="st-actual">Actual costs</Label>
                <Input
                  id="st-actual"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={actual}
                  onChange={(e) => setActual(e.target.value)}
                  placeholder="0.00"
                  className="h-9 text-right tabular-nums"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="st-advanced">Advances paid (optional)</Label>
                <Input
                  id="st-advanced"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={advanced}
                  onChange={(e) => setAdvanced(e.target.value)}
                  placeholder="auto"
                  className="h-9 text-right tabular-nums"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="st-note">Note</Label>
              <Input
                id="st-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. 2025 service cost settlement"
                className="h-9"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => {
                  setOpen(false);
                  reset();
                }}
              >
                Cancel
              </Button>
              <Button size="sm" type="submit" disabled={create.isPending}>
                {create.isPending ? "Recording…" : "Record settlement"}
              </Button>
            </div>
          </form>
        )}

        {rows.length > 0 ? (
          <div className="space-y-2 border-t border-border/60 pt-4">
            {rows.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-3 border-b border-border/60 pb-2 text-sm last:border-0"
              >
                <div>
                  <p className="font-medium">
                    {s.period_start} → {s.period_end}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Advances {euro(s.total_advanced)} · Actual {euro(s.total_actual)}
                    {s.note ? ` · ${s.note}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-right">
                  <span className="tabular-nums font-medium">
                    {s.direction === "refund" ? "−" : ""}
                    {euro(Math.abs(s.difference))}
                  </span>
                  <DirectionBadge direction={s.direction} />
                  {s.invoice_id && (
                    <Link
                      to={`/invoices/${s.invoice_id}`}
                      className="text-xs font-medium text-accent hover:underline"
                    >
                      Invoice
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          !open && (
            <p className="text-sm text-muted-foreground">No settlements yet.</p>
          )
        )}
      </CardContent>
    </Card>
  );
}
